import { Checkbox } from "@/components/elements/Checkbox";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { InnerBox } from "@/components/elements/InnerBox";
import { Table } from "@/components/elements/Table";
import { useTable } from "@/hooks/useTable";
import { text } from "@/theme/colors";
import { formatDateOrDash } from "@/utils/dayjs";
import { productAccessSortableHeader } from "@/features/customer-user/components/productAccessSortableHeader";
import { useSelectableProductContracts } from "@/features/customer-user/hooks/useSelectableProductContracts";
import { useSortedProductAccess } from "@/features/customer-user/hooks/useSortedProductAccess";
import { Box, Stack, Typography, styled } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useEffect, useMemo, useRef } from "react";
import { Controller, Control, useFormContext } from "react-hook-form";
import { TCustomerUserForm } from "@/features/customer-user/types/customerUserForm";
import { TCustomerUserDetailDto } from "@/api/services/main/customer-user/dtos/customer-user.dto";

type TProductAccessRow = {
  id: number;
  productCode: string;
  productName: string;
  activeUserCount: string;
  lastLoginAt: string;
  startedAt: string;
  editable: boolean;
};

export const CustomerUserProductAccessFields: FC<{ control: Control<TCustomerUserForm>; user?: TCustomerUserDetailDto }> = ({ control, user }) => {
  const { selectableContracts } = useSelectableProductContracts();

  const accessByCode = useMemo(() => new Map((user?.productAccess ?? []).map((item) => [item.productCode as string, item])), [user?.productAccess]);

  const rows = useMemo<TProductAccessRow[]>(
    () =>
      selectableContracts.map((c) => {
        const access = accessByCode.get(c.product.code);
        return {
          id: c.id,
          productCode: c.product.code,
          productName: c.product.name,
          activeUserCount: String(c.activeUserCount),
          lastLoginAt: formatDateOrDash(access?.lastLoginAt),
          startedAt: formatDateOrDash(access?.createdAt),
          editable: true,
        };
      }),
    [selectableContracts, accessByCode],
  );

  const sortedRows = useSortedProductAccess(rows);

  const selectableCodes = useMemo(() => rows.filter((row) => row.editable).map((row) => row.productCode), [rows]);

  const {
    setValue,
    formState: { errors },
  } = useFormContext<TCustomerUserForm>();
  const autoSelected = useRef(false);

  useEffect(() => {
    if (user || autoSelected.current || selectableCodes.length !== 1) return;
    autoSelected.current = true;
    setValue("productCodes", selectableCodes, { shouldDirty: false });
  }, [user, selectableCodes, setValue]);

  const columns = useMemo<MRT_ColumnDef<TProductAccessRow>[]>(
    () => [
      {
        header: "",
        id: "access",
        size: 48,
        Header: () =>
          selectableCodes.length ? (
            <Controller
              control={control}
              name="productCodes"
              render={({ field }) => {
                const allSelected = selectableCodes.every((code) => field.value.includes(code));
                return (
                  <Checkbox
                    nonControl
                    checked={allSelected}
                    onChange={(_, next) =>
                      field.onChange(
                        next
                          ? Array.from(new Set([...field.value, ...selectableCodes]))
                          : field.value.filter((code) => !selectableCodes.includes(code)),
                      )
                    }
                  />
                );
              }}
            />
          ) : null,
        Cell: ({ row }) =>
          row.original.editable ? (
            <Controller
              control={control}
              name="productCodes"
              render={({ field }) => {
                const productCode = row.original.productCode;
                const checked = field.value.includes(productCode);
                return (
                  <Checkbox
                    nonControl
                    checked={checked}
                    onChange={(_, next) => field.onChange(next ? [...field.value, productCode] : field.value.filter((code) => code !== productCode))}
                  />
                );
              }}
            />
          ) : (
            <Checkbox nonControl checked={false} disabled />
          ),
      },
      {
        header: "プロダクト名",
        id: "productName",
        accessorKey: "productName",
        size: 300,
        Header: productAccessSortableHeader("productName", "プロダクト名"),
        Cell: ({ row }) => <EllipsisText title={row.original.productName} variant="body14" maxWidth={284} />,
      },
      { header: "有効ユーザー数", id: "activeUserCount", accessorKey: "activeUserCount" },
      { header: "最終ログイン", id: "lastLoginAt", accessorKey: "lastLoginAt", Header: productAccessSortableHeader("lastLoginAt", "最終ログイン") },
      { header: "ご利用開始日", id: "startedAt", accessorKey: "startedAt", Header: productAccessSortableHeader("startedAt", "ご利用開始日") },
    ],
    [control, selectableCodes],
  );

  const { table } = useTable<TProductAccessRow>({ columns, rows: sortedRows, getRowId: (row) => String(row.id) });

  return (
    <InnerBox title="プロダクトアクセス権限" padding="24px">
      <Stack gap="16px">
        <CompactHeaderTable>
          <Table table={table} />
        </CompactHeaderTable>
        <Typography variant="cap12" color={text.secondary}>
          プロダクトのご契約変更については、右下のチャットよりお問い合わせください。
        </Typography>
        {/* The table has no input of its own to hang a helper text on, so the field error lives here. */}
        {errors.productCodes?.message && (
          <Typography variant="cap12" color={text.error}>
            {errors.productCodes.message}
          </Typography>
        )}
      </Stack>
    </InnerBox>
  );
};

const CompactHeaderTable = styled(Box)`
  .MuiTableCell-head {
    font-size: 14px;
  }
`;
